import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useT, userTz } from "@/lib/i18n";
import { API } from "@/lib/api";
import { FAQ } from "@/i18n/strings";
import { useConfig } from "@/components/Layout";

const LINK_RE = /(https?:\/\/[^\s)]+|\/(?:en|ro|es|fr|it|pt)(?:\/[\w\-/]*)?|\/sign-in)/g;
function Linkify({ text }) {
  return String(text).split(LINK_RE).map((part, k) => {
    if (k % 2 === 0) return part;
    const clean = part.replace(/[.,;:!?]+$/, "");
    const tail = part.slice(clean.length);
    return clean.startsWith("http")
      ? <span key={k}><a href={clean} target="_blank" rel="noopener noreferrer" className="underline font-semibold" data-testid="guide-ext-link">{clean}</a>{tail}</span>
      : <span key={k}><Link to={clean} className="underline font-semibold" data-testid="guide-int-link">{clean}</Link>{tail}</span>;
  });
}

function Dialog({ title, onClose, children, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const focusables = () => el.querySelectorAll("button, a, input, textarea, [tabindex]:not([tabindex='-1'])");
    focusables()[0]?.focus();
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        const f = focusables(); const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", key);
    const main = document.getElementById("main"); main?.setAttribute("inert", "");
    return () => { document.removeEventListener("keydown", key); main?.removeAttribute("inert"); };
  }, [onClose]);
  return (
    <div ref={ref} role="dialog" aria-modal="true" aria-label={title} data-testid={testid}
      className="fixed z-[70] right-0 md:right-6 bottom-0 md:bottom-6 w-full md:max-w-[360px] max-h-[80vh] flex flex-col card !rounded-b-none md:!rounded-b-[20px] overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 bg-[var(--forest)] text-[var(--ivory)]">
        <h2 className="text-xl">{title}</h2>
        <button onClick={onClose} aria-label="Close" className="min-w-[44px] min-h-[44px] grid place-items-center" data-testid={`${testid}-close`}><X /></button>
      </div>
      {children}
    </div>
  );
}

function faqAnswer(q, px) {
  const words = q.toLowerCase().split(/\W+/).filter((w) => w.length > 3);
  let best = null, score = 0;
  FAQ.forEach((f, k) => { const txt = px(f, "FAQ", k).toLowerCase(); const s = words.filter((w) => txt.includes(w)).length; if (s > score) { score = s; best = px(f, "FAQ", k).split("|")[1]; } });
  return score > 0 ? best : null;
}

export function GuidePanel({ onClose }) {
  const { t, tl, px, locale, lp } = useT();
  const cfg = useConfig();
  const [msgs, setMsgs] = useState([{ role: "assistant", text: t("guideGreeting") }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const ai = cfg.guide_mode === "ai";
  const send = async (text) => {
    const q = text.trim().slice(0, 800);
    if (!q || busy) return;
    const next = [...msgs, { role: "user", text: q }];
    setMsgs(next); setInput("");
    if (!ai) { setMsgs([...next, { role: "assistant", text: faqAnswer(q, px) || t("guideUnknown") }]); return; }
    setBusy(true);
    let acc = "";
    try {
      const res = await fetch(`${API}/guide/messages`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ locale, messages: next.slice(-8) }) });
      if (!res.ok) throw new Error();
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { done, value } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const d = p.replace(/^data: /, "");
          if (d === "[DONE]" || d === "[ERROR]") continue;
          acc += d.replace(/\\n/g, "\n");
          setMsgs([...next, { role: "assistant", text: acc }]);
        }
      }
      if (!acc) throw new Error();
    } catch { setMsgs([...next, { role: "assistant", text: faqAnswer(q, px) || t("guideUnknown") }]); }
    setBusy(false);
  };
  return (
    <Dialog title={t("guideTitle")} onClose={onClose} testid="guide-panel">
      <p className="text-xs px-5 pt-3 text-[var(--muted)]">{ai ? "AI · Claude" : t("guideFaqMode")} — {t("guidePrivacy")}</p>
      <div className="flex-1 overflow-y-auto px-5 py-3 grid gap-3 content-start" aria-live="polite" data-testid="guide-messages">
        {msgs.map((m, k) => <p key={k} className={`text-sm rounded-2xl px-4 py-3 whitespace-pre-wrap min-w-0 break-words [overflow-wrap:anywhere] ${m.role === "user" ? "bg-[var(--forest)] text-[var(--ivory)] ml-8" : "bg-[var(--ivory)] mr-8"}`}>{m.role === "user" ? m.text : <Linkify text={m.text} />}</p>)}
        {msgs.length === 1 && <div className="flex flex-wrap gap-2">{tl("guideChips").map((c) => <button key={c} className="chip text-xs" onClick={() => send(c)}>{c}</button>)}</div>}
        <Link to={lp("/contact")} className="text-xs underline" onClick={onClose}>{tl("nav")[10]}</Link>
      </div>
      <form className="flex gap-2 p-3 border-t" onSubmit={(e) => { e.preventDefault(); send(input); }}>
        <label className="sr-only" htmlFor="guide-input">{t("guidePlaceholder")}</label>
        <input id="guide-input" value={input} maxLength={800} onChange={(e) => setInput(e.target.value)} placeholder={t("guidePlaceholder")} className="flex-1 border rounded-full px-4 text-sm min-h-[44px]" data-testid="guide-input" />
        <button className="btn btn-gold btn-sm" disabled={busy} data-testid="guide-send">{t("send")}</button>
      </form>
    </Dialog>
  );
}

export function WhatsAppPanel({ onClose }) {
  const { t, tl, locale } = useT();
  const cfg = useConfig();
  const items = tl("wa");
  const num = cfg.whatsapp_number || "68987311369";
  const tz = userTz();
  return (
    <Dialog title={t("waTitle")} onClose={onClose} testid="wa-panel">
      <div className="p-5 grid gap-2 overflow-y-auto">
        <p className="text-xs text-[var(--muted)]">{t("waNote")} {cfg.business_hours_note?.[locale]}</p>
        {[0, 2, 4, 6, 8].map((k, n) => {
          const text = items[k + 1].replace("[language]", locale.toUpperCase()).replace("[limba]", "română").replace("[time zone]", tz).replace("[fusul orar]", tz);
          return <a key={k} href={`https://wa.me/${num}?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" className="chip justify-between" data-testid={`wa-option-${n + 1}`}>{items[k]}</a>;
        })}
      </div>
    </Dialog>
  );
}
